import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalFollowUpDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

export type { PortalFollowUpDto };

/**
 * GitHub #34 — the per-report "Portal follow-ups" checklist. Portal-handoff obligations the
 * electronic filing itself can't satisfy (an AL In-Kind "Other" row keyed by hand in the FCPA
 * portal, a refund's record-then-amend step, a Major Contribution Report, per-loan guarantors,
 * the portal-only AL amendment) are snapshotted at file time so a treasurer returning later
 * still sees what they owe. Campaign-scoped query key.
 */
export function usePortalFollowUps(reportId: string | undefined) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.portalFollowUps(reportId ?? ''),
    queryFn: () =>
      apiJson<PortalFollowUpDto[]>(`/api/finance/reports/${reportId}/portal-follow-ups`, {
        error: 'Failed to fetch portal follow-ups',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
  });
}

/**
 * Marks a portal follow-up done (treasurer handled it in the portal). Invalidates the report's
 * checklist so the item flips to muted/checked. Destructures `mutate`/`mutateAsync` per the
 * finance golden rule (never pass the whole mutation object to a deps array).
 */
export function useCompletePortalFollowUp() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: ({ reportId, followUpId }: { reportId: string; followUpId: string }) =>
      apiJson<PortalFollowUpDto>(
        `/api/finance/reports/${reportId}/portal-follow-ups/${followUpId}/complete`,
        { method: 'POST', error: 'Failed to mark the follow-up done' },
      ),
    onSuccess: (_result, { reportId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.reports.portalFollowUps(reportId),
      });
    },
  });

  return { completeFollowUp: mutate, completeFollowUpAsync: mutateAsync, isCompleting: isPending };
}

/** Reopens a completed portal follow-up (back to Open). Invalidates the report's checklist. */
export function useReopenPortalFollowUp() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: ({ reportId, followUpId }: { reportId: string; followUpId: string }) =>
      apiJson<PortalFollowUpDto>(
        `/api/finance/reports/${reportId}/portal-follow-ups/${followUpId}/reopen`,
        { method: 'POST', error: 'Failed to reopen the follow-up' },
      ),
    onSuccess: (_result, { reportId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.reports.portalFollowUps(reportId),
      });
    },
  });

  return { reopenFollowUp: mutate, reopenFollowUpAsync: mutateAsync, isReopening: isPending };
}
